import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NestedField } from 'react-form';
import moment from 'moment-timezone';

import { required } from '../../../../utils/form-validation';
import Tip from '../../../Tip';
import { HighSchoolSearchAndInvitePopup } from '../../../PopUps';
import {
    SchoolSearch,
    Select,
    Input,
    CheckBox,
    DateTimePicker,
} from '../../../Form';
import { VerifiedRulesLink } from '../../../Links';

import { getApiAxiosInstance } from '../../../../utils/network';
import { getTimeZones } from '../../../../utils/events';
import { inviteCoachToClaimSchool } from '../../../../api/schoolsApi';
import { getAllClubLocations } from '../../../../api/clubLocationsApi';
import SelectIsConference from './SelectIsConference';
import { getDualMatchSupportedSubTypes } from '../../../Clubs/Popups/PostDualMatchPopup/helpers';

const DualMatch = (props) => {
    const {
        editing,
        defaultValues,
        formApi,
        clubSchoolProfile,
        showTip,
        clubTierType,
        closeCreatePage,
        lastEventIds,
        clubId,
        showSelectIsConference,
        clubLocation,
        clubSubType,
    } = props;

    const coreUITimeZones = useSelector((state) => state.coreUI.types.timeZones);
    const { values } = formApi;
    const [dualMatchSelectedOpponentSchool, setDualMatchSelectedOpponentSchool] = useState(null);
    const [showSchoolSearchAndInvitePopup, setShowSchoolSearchAndInvitePopup] = useState(false);
    const [dualMatchLocationOptions, setDualMatchLocationOptions] = useState([]);
    const [dualMatchEventScheduleStartDate, setDualMatchEventScheduleStartDate] = useState(null);
    const [timezone, setTimezone] = useState(null);
    const [message, setMessage] = useState('');
    const [showTimezone, setShowTimezone] = useState(false);
    const [clubLocations, setClubLocations] = useState([]);

    useEffect(() => {
        const timeZone = defaultValues?.eventSchedule?.timeZone || 77;
        const timezoneObj = coreUITimeZones.find(tz => { return tz.id === parseInt(timeZone); });
        setDualMatchEventScheduleStartDate(defaultValues.eventSchedule ? moment(defaultValues.eventSchedule.eventStartUtc).tz(timezoneObj.value) : null);
        setTimezone(timeZone);

        (async () => {
            const { data } = await getAllClubLocations(getApiAxiosInstance(), values.club);
            setClubLocations(data.clubLocations);
        })();
    }, []);

    const handleClearSchoolClubClick = () => {
        formApi.setValue('name', null);
        formApi.setValue('dualMatchClubs', null);
        formApi.setValue('clubLocationId', null);
        formApi.setValue('eventLocations', defaultValues.eventLocations);

        setDualMatchSelectedOpponentSchool(null);
    };

    const setLocationOptions = async (opponentSchoolClub) => {
        const { data } = await getAllClubLocations(getApiAxiosInstance(), opponentSchoolClub.id);

        const locationOptions = [];
        function createLocationOption(location, locationClubId, locationClubSchoolProfile) {
            return {
                id: location.id,
                clubId: locationClubId,
                value: location.location.googleFormattedName,
                label: `${location.name} - ${locationClubSchoolProfile.displayName}`,
            };
        }

        // current club locations
        clubLocations.forEach(l => {
            locationOptions.push(createLocationOption(l, values.club, clubSchoolProfile));
        });
        // opponent club locations
        data.clubLocations.forEach(l => {
            locationOptions.push(createLocationOption(l, opponentSchoolClub.id, opponentSchoolClub.school));
        });

        setDualMatchLocationOptions(locationOptions);
    };

    const handleSchoolClubSelect = (opponentSchoolClub, currentClubIsHome, opponentClubIsHome) => {
        const currentSchoolClubName = clubSchoolProfile.displayName;
        const opponentSchoolClubName = opponentSchoolClub.school.displayName;
        const dualMatchClubs = [{
            clubId: values.club,
            isHome: currentClubIsHome,
        }, {
            clubId: opponentSchoolClub.id,
            isHome: opponentClubIsHome,
        }];

        formApi.setValue('dualMatchClubs', dualMatchClubs);

        if (currentClubIsHome) {
            formApi.setValue('name', `${opponentSchoolClubName} at ${currentSchoolClubName} `);
        } else if (opponentClubIsHome) {
            formApi.setValue('name', `${currentSchoolClubName} at ${opponentSchoolClubName} `);
        } else {
            formApi.setValue('name', `${opponentSchoolClubName} vs. ${currentSchoolClubName} `);
        }

        setDualMatchSelectedOpponentSchool(opponentSchoolClub);
        setLocationOptions(opponentSchoolClub);
    };

    const toggleTimezone = (show, val) => {
        const date = dualMatchEventScheduleStartDate;
        if (val && formApi) {
            const timezoneObj = coreUITimeZones.find(tz => { return tz.id === parseInt(val); });
            date.tz(timezoneObj.value, true);
            formApi.setValue('eventSchedule.eventStartUtc', date);
        }

        setShowTimezone(show);
        setDualMatchEventScheduleStartDate(date);

        if (val) {
            setTimezone(parseInt(val));
        }
    };

    const handleDualMatchSchedule = (date) => {
        if (clubTierType.isHighSchool) {
            const isDateOutOfRange = date < moment().add('months', -3).add('days', -1);
            if (!isDateOutOfRange) {
                formApi.setValue('eventSchedule.registrationStartUtc', date);
                date.add('days', -8);
                formApi.setValue('eventSchedule.registrationEndUtc', date);
                date.add('days', 7);
                formApi.setValue('eventSchedule.eventEndUtc', date);
                date.add('days', 1);
                setMessage('');
            } else {
                const today = moment();
                formApi.setValue('eventSchedule.eventStartUtc', today);
                formApi.setValue('eventSchedule.registrationStartUtc', today);
                today.add('days', -8);
                formApi.setValue('eventSchedule.registrationEndUtc', today);
                today.add('days', 7);
                formApi.setValue('eventSchedule.eventEndUtc', today);
                today.add('days', 1);
                setMessage(`The event will default to ${today.format('MMM Do YYYY')} if a date before ${moment().add('months', -3).format('MMM Do YYYY')} is selected.`);
            }
        }
        setDualMatchEventScheduleStartDate(date);
    };

    const handleDualMatchLocationChange = (optionValue, e) => {
        e.preventDefault();
        const selectedMatchLocationOption = dualMatchLocationOptions.find((option) => {
            return option.id === optionValue;
        });
        const clubIdOfLocation = selectedMatchLocationOption.clubId;
        const currentClubIsHome = clubIdOfLocation === defaultValues.club;
        const opponentClubIsHome = clubIdOfLocation === dualMatchSelectedOpponentSchool.id;

        formApi.setValue(['eventLocations', 0, 'location'], selectedMatchLocationOption.value);
        handleSchoolClubSelect(dualMatchSelectedOpponentSchool, currentClubIsHome, opponentClubIsHome);
    };

    const handleSubmitSchoolSearchAndInvite = async (vals) => {
        const result = await inviteCoachToClaimSchool(getApiAxiosInstance(), clubId, vals.schoolId, vals.email);
        return result;
    };

    const timeZones = getTimeZones(coreUITimeZones);

    const getTipContent = () => {
        return (
            <p>
                {clubTierType.isHighSchool ? 'High School dual matches are Verified Events.' : ''}
                &nbsp;
                Verified events can only be run by Verified Event Organizers, Clubs, Colleges, or High Schools and must follow the
                &nbsp;
                <VerifiedRulesLink target="_blank">Verified Event Rules</VerifiedRulesLink>
            </p>
        );
    };

    return (
        <React.Fragment>
            <div className={`col-12 mt24 mb24`}>
                <div className="form-group form-group-xl">
                    <label>Opponent High School</label>
                    <SchoolSearch
                        placeholder={`Search for your opponent high school`}
                        handleSchoolSelect={(schoolClub) => handleSchoolClubSelect(schoolClub, true, false)}
                        clearAfterSelect={true}
                        closeAfterSelect={true}
                        displaySchoolNameAfterSelect={true}
                        showSearchClearIcon={true}
                        searchEndPoint={`/highSchools`}
                        entityId={defaultValues.club}
                        schoolClubSearch={true}
                        handleClearSchoolClick={handleClearSchoolClubClick}
                        showInviteSchool={true}
                        onInviteSchoolClick={() => {
                            handleClearSchoolClubClick();
                            setShowSchoolSearchAndInvitePopup(true);
                        }}
                        isProximitySearch={true}
                        clubLocation={clubLocation}
                        clubSubTypeIds={getDualMatchSupportedSubTypes(clubSubType)}
                    />
                </div>
                {showSelectIsConference && (
                    <SelectIsConference
                        field="$isConference"
                        value={values.isConference}
                        formApi={formApi}
                    />
                )}
            </div>
            {values.dualMatchClubs && values.dualMatchClubs.length > 0 && (
                <React.Fragment>
                    <div className={`col-12 mb24`}>
                        <div className="div div16" />
                        <Input
                            label="Match Name"
                            id="name"
                            field="name"
                            type="hidden"
                        />
                    </div>
                    <div className={`col-12 mb24`}>
                        <NestedField field="eventSchedule" validateOnSubmit={false}>
                            <React.Fragment>
                                <DateTimePicker
                                    label="Date and Time"
                                    id="matchStart"
                                    field="eventStartUtc"
                                    placeholderText="Select date"
                                    selected={dualMatchEventScheduleStartDate}
                                    minDate={clubTierType.isHighSchool ? moment().add('months', -3) : moment().add('years', -1)}
                                    maxDate={null}
                                    handleDateChange={(date) => handleDualMatchSchedule(date)}
                                    validate={required}
                                />
                                {message && <div className="pb24 text-center text-error text-small">{message}</div>}
                                <p className="text-small"><strong>Timezone: </strong>
                                    {(timezone ? timeZones.find(tz => tz.id === parseInt(timezone)).label : '')}
                                    <a
                                        href="#"
                                        className="underline"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            toggleTimezone(true);
                                        }}
                                    > change
                                    </a>
                                </p>
                                {showTimezone
                                    ? (
                                        <Select
                                            id="timezone"
                                            hiddenLabel={true}
                                            hiddenDefault={true}
                                            field="timeZone"
                                            options={timeZones}
                                            onChange={(val) => toggleTimezone(false, val)}
                                        />
                                    ) : null}
                            </React.Fragment>
                        </NestedField>
                        <Tip
                            variant="info"
                            content={getTipContent()}
                            showTip={showTip}
                            isComponent={true}
                        />
                    </div>
                    <div className={`col-12 mb24`}>
                        <Select
                            key={`match-location`}
                            id={`match-location`}
                            label="Location"
                            field={`clubLocationId`}
                            value={
                                dualMatchLocationOptions && dualMatchLocationOptions.length > 0 ? dualMatchLocationOptions[0] : ''
                            }
                            onChange={(optionValue, e) => handleDualMatchLocationChange(optionValue, e)}
                            hiddenDefault={true}
                            options={dualMatchLocationOptions}
                            validate={required}
                        />
                        <Input field={['eventLocations', 0, 'eventLocationType']} type="hidden" />
                    </div>

                    {lastEventIds && lastEventIds[values.eventType] && (
                        <div className={`col-12 mb24`}>
                            <CheckBox
                                id={`cloneLastEvent`}
                                customLabel={`Use my last event as a template`}
                                field={`cloneLastEvent`}
                                formGroupCssClass="mb16"
                            />
                        </div>
                    )}
                </React.Fragment>
            )}
            {!editing && showSchoolSearchAndInvitePopup && (
                <HighSchoolSearchAndInvitePopup
                    currentSchoolId={clubSchoolProfile.id}
                    onCloseClick={() => {
                        setShowSchoolSearchAndInvitePopup(false);
                    }}
                    onCtaClickAsync={handleSubmitSchoolSearchAndInvite}
                    navigateAwayFromPage={closeCreatePage}
                />
            )}
        </React.Fragment>
    );
};

export default DualMatch;
